import axios from "axios";
import environments from "../environment";
import { proxyStorage } from "../utils/proxyStorage";

const isLocalBrowser =
	typeof window !== "undefined" && ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
const telemetryBaseURL =
	(isLocalBrowser ? environments.localApiRoot || environments.apiRoot : environments.apiRoot || environments.localApiRoot) || "";

export const adminTelemetryEndpoint = "/station-portal/logs";

const SOURCE_APP = "whalelo-admin";
const SESSION_STORAGE_KEY = "whalelo-admin-log-session-id";
const DUPLICATE_WINDOW_MS = 1500;
const recentEventMap = new Map<string, number>();

const telemetryClient = axios.create({
	baseURL: telemetryBaseURL,
	withCredentials: true,
});

telemetryClient.interceptors.request.use((config) => {
	const accessToken = proxyStorage()?.getItem("token");
	const headers: any = config.headers || {};
	if (accessToken && !headers?.Authorization) {
		headers.Authorization = `Bearer ${accessToken}`;
	}
	config.headers = headers;
	return config;
});

export type AdminTelemetryPayload = {
	eventType: string;
	level?: "info" | "warn" | "error";
	action: string;
	message?: string | null;
	route?: string | null;
	requestPath?: string | null;
	requestMethod?: string | null;
	statusCode?: number | null;
	actorEmail?: string | null;
	metadata?: Record<string, any> | null;
	occurredAt?: string;
};

function truncate(value: any, maxLength = 255) {
	if (value == null) return null;
	const normalized = String(value).trim();
	if (!normalized) return null;
	return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
}

function sanitizeMetadata(value: any, depth = 0): any {
	if (value == null) return null;
	if (depth > 3) return "[max-depth]";
	if (Array.isArray(value)) {
		return value.slice(0, 30).map((item) => sanitizeMetadata(item, depth + 1));
	}
	if (value instanceof Date) {
		return value.toISOString();
	}
	if (typeof value === "object") {
		return Object.entries(value)
			.slice(0, 40)
			.reduce((acc, [key, childValue]) => {
				acc[key] = sanitizeMetadata(childValue, depth + 1);
				return acc;
			}, {} as Record<string, any>);
	}
	if (typeof value === "string") {
		return truncate(value, 2000);
	}
	if (["number", "boolean"].includes(typeof value)) {
		return value;
	}
	return truncate(value, 500);
}

function getCurrentRoute() {
	if (typeof window === "undefined") return null;
	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getSessionId() {
	if (typeof window === "undefined") return "server";
	try {
		const existed = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
		if (existed) return existed;
		const created = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
		window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
		return created;
	} catch (error) {
		return "session-unavailable";
	}
}

function shouldSkipDuplicate(payload: AdminTelemetryPayload) {
	const fingerprint = [
		payload.eventType,
		payload.action,
		payload.route || getCurrentRoute() || "",
		payload.requestPath || "",
		payload.statusCode || "",
		payload.message || "",
	].join("|");

	const now = Date.now();
	const lastSeenAt = recentEventMap.get(fingerprint) || 0;
	recentEventMap.set(fingerprint, now);

	if (recentEventMap.size > 250) {
		const cutoff = now - DUPLICATE_WINDOW_MS;
		Array.from(recentEventMap.entries()).forEach(([key, seenAt]) => {
			if (seenAt < cutoff) {
				recentEventMap.delete(key);
			}
		});
	}

	return now - lastSeenAt < DUPLICATE_WINDOW_MS;
}

export async function trackAdminEvent(payload: AdminTelemetryPayload): Promise<void> {
	try {
		if (!telemetryBaseURL || !payload?.eventType || !payload?.action) return;
		if (shouldSkipDuplicate(payload)) return;

		await telemetryClient.post(adminTelemetryEndpoint, {
			sourceApp: SOURCE_APP,
			eventType: truncate(payload.eventType, 80),
			level: truncate(payload.level || "info", 40),
			action: truncate(payload.action, 120),
			message: truncate(payload.message, 4000),
			route: truncate(payload.route || getCurrentRoute(), 500),
			requestPath: truncate(payload.requestPath, 1000),
			requestMethod: truncate(payload.requestMethod, 20),
			statusCode: payload.statusCode || null,
			actorEmail: truncate(payload.actorEmail, 255),
			occurredAt: payload.occurredAt || new Date().toISOString(),
			metadata: sanitizeMetadata({
				...(payload.metadata || {}),
				sessionId: getSessionId(),
				currentHref: typeof window !== "undefined" ? truncate(window.location.href, 1000) : null,
			}),
		});
	} catch (_) {
		// Swallow telemetry failures
	}
}

export async function trackAdminHttpError(error: any): Promise<void> {
	const requestUrl = String(error?.config?.url || "");
	if (!requestUrl || requestUrl.includes(adminTelemetryEndpoint)) return;

	const statusCode = error?.response?.status || null;
	const responseMessage =
		error?.response?.data?.message || error?.message || "Admin frontend request failed";

	await trackAdminEvent({
		eventType: "api_error",
		level: statusCode && statusCode >= 500 ? "error" : "warn",
		action: "api_request_failed",
		message: responseMessage,
		requestPath: requestUrl,
		requestMethod: error?.config?.method,
		statusCode,
		metadata: {
			responseName: error?.response?.data?.name || null,
			responseCode: error?.response?.data?.code || null,
			baseURL: error?.config?.baseURL || null,
		},
	});
}
